// pages/PromoCode/AllPromoCodes.js
import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import PromoCodeModal from "components/PromoCode/PromoCodeModal";
import DeletePromoModal from "components/PromoCode/DeletePromoModal";
import { promoCodeApiService } from "services/promoCodeApiService";

const AllPromoCodes = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch promo codes
  const fetchPromoCodes = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await promoCodeApiService.getAllPromoCodes();
      setPromoCodes(data);
    } catch (error) {
      toast.error("Failed to fetch promo codes");
      console.error("Error fetching promo codes:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  // Handle create/update promo code
  const handleSavePromoCode = async (formData) => {
    try {
      if (selectedPromo) {
        // Update existing promo
        await promoCodeApiService.updatePromoCode(selectedPromo.id, formData);
        toast.success("Promo code updated successfully");
      } else {
        // Create new promo
        await promoCodeApiService.createPromoCode(formData);
        toast.success("Promo code created successfully");
      }

      fetchPromoCodes(false);
      setModalOpen(false);
      setSelectedPromo(null);
    } catch (error) {
      toast.error(selectedPromo ? "Failed to update promo code" : "Failed to create promo code");
      console.error("Error saving promo code:", error);
      throw error; // Re-throw for modal to handle
    }
  };

  // Handle delete promo code
  const handleDeletePromoCode = async () => {
    if (!selectedPromo) return;

    try {
      await promoCodeApiService.deletePromoCode(selectedPromo.id);
      toast.success("Promo code deleted successfully");
      fetchPromoCodes(false);
      setDeleteModalOpen(false);
      setSelectedPromo(null);
    } catch (error) {
      toast.error("Failed to delete promo code");
      console.error("Error deleting promo code:", error);
      throw error; // Re-throw for modal to handle
    }
  };

  // Handle edit click
  const handleEditClick = (promoCode) => {
    setSelectedPromo(promoCode);
    setModalOpen(true);
  };

  // Handle delete click
  const handleDeleteClick = (promoCode) => {
    setSelectedPromo(promoCode);
    setDeleteModalOpen(true);
  };

  // Handle create click
  const handleCreateClick = () => {
    setSelectedPromo(null);
    setModalOpen(true);
  };

  // Filter promo codes based on search
  const filteredPromoCodes = useMemo(() => {
    if (!searchQuery.trim()) return promoCodes;

    const query = searchQuery.toLowerCase();
    return promoCodes.filter(promo =>
      promo.name.toLowerCase().includes(query) ||
      promo.codename.toLowerCase().includes(query) ||
      (promo.codemoney && promo.codemoney.toString().includes(query))
    );
  }, [promoCodes, searchQuery]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => `#${row.original.id}`,
      },
      {
        header: "Promo Name",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{row.original.name}</div>
            <small className="text-muted">Created: {formatDate(row.original.date)}</small>
          </div>
        ),
      },
      {
        header: "Promo Code",
        accessorKey: "codename",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <code className="fw-bold text-primary">{row.original.codename}</code>
          </div>
        ),
      },
      {
        header: "Type",
        accessorKey: "freedelivery",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            {row.original.freedelivery ? (
              <span className="badge bg-success">
                <i className="fas fa-truck me-1"></i> Free Delivery
              </span>
            ) : (
              <span className="badge bg-warning">
                <i className="fas fa-tag me-1"></i> Discount
              </span>
            )}
          </div>
        ),
      },
      {
        header: "Value",
        accessorKey: "codemoney",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="fw-semibold">
            {row.original.freedelivery ? (
              <span className="text-success">FREE Delivery</span>
            ) : (
              <>{row.original.codemoney || "0"}%</>
            )}
          </div>
        ),
      },
      // {
      //   header: "Status",
      //   accessorKey: "status",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }) => {
      //     const promoDate = new Date(row.original.date);
      //     const now = new Date();
      //     const diffTime = Math.abs(now - promoDate);
      //     const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      //     if (diffDays > 365) {
      //       return <span className="badge bg-danger">Expired</span>;
      //     } else if (diffDays > 180) {
      //       return <span className="badge bg-warning">Expiring Soon</span>;
      //     } else {
      //       return <span className="badge bg-success">Active</span>;
      //     }
      //   },
      // },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              onClick={() => handleEditClick(row.original)}
              className="btn btn-primary btn-sm"
              title="Edit"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              onClick={() => handleDeleteClick(row.original)}
              className="btn btn-danger btn-sm"
              title="Delete"
            >
              <i className="fas fa-trash"></i>
            </button>
            {/* <button
              onClick={() => {
                navigator.clipboard.writeText(row.original.codename);
                toast.success("Promo code copied to clipboard!");
              }}
              className="btn btn-info btn-sm"
              title="Copy Code"
            >
              <i className="fas fa-copy"></i>
            </button> */}
          </div>
        ),
      },
    ],
    []
  );

  // Statistics
  const totalPromoCodes = promoCodes.length;
  const freeDeliveryCount = promoCodes.filter(p => p.freedelivery).length;
  const discountCount = promoCodes.filter(p => !p.freedelivery).length;
  const totalDiscountValue = promoCodes
    .filter(p => !p.freedelivery && p.codemoney)
    .reduce((sum, p) => sum + parseFloat(p.codemoney), 0);

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Promo Codes" breadcrumbItem="Manage Promo Codes" />

        {/* Statistics Cards */}
        {/* <div className="row mb-4">
          <div className="col-md-3">
            <div className="card border-primary border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Total Promo Codes</p>
                    <h4 className="mt-2">{totalPromoCodes}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-primary rounded-circle">
                      <i className="fas fa-tags font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-success border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Free Delivery</p>
                    <h4 className="mt-2">{freeDeliveryCount}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-success rounded-circle">
                      <i className="fas fa-truck font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-warning border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Discount Promos</p>
                    <h4 className="mt-2">{discountCount}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-warning rounded-circle">
                      <i className="fas fa-percentage font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-info border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Total Discount Value</p>
                    <h4 className="mt-2">৳{totalDiscountValue.toFixed(2)}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-info rounded-circle">
                      <i className="fas fa-money-bill-wave font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Action Bar */}
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2 align-items-center">
            <div className="input-group" style={{ width: "350px" }}>
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, code, or amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setSearchQuery("")}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-success"
              onClick={() => fetchPromoCodes(false)}
              disabled={refreshing}
              title="Refresh"
            >
              <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCreateClick}
            >
              <i className="fas fa-plus me-2"></i>
              Create Promo Code
            </button>
          </div>
        </div>

        {/* Promo Codes Table */}
        <div className="card">
          <div className="card-body">
            {filteredPromoCodes.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-tags fa-3x text-muted mb-3"></i>
                <h4>No promo codes found</h4>
                <p className="text-muted mb-4">
                  {searchQuery ? 'Try a different search term' : 'Create your first promo code to get started'}
                </p>
                {!searchQuery && (
                  <button
                    className="btn btn-primary"
                    onClick={handleCreateClick}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Create First Promo Code
                  </button>
                )}
              </div>
            ) : (
              <TableContainer
                columns={columns}
                data={filteredPromoCodes}
                isGlobalFilter={false}
                isPagination={true}
                pagination="pagination"
                paginationWrapper="dataTables_paginate paging_simple_numbers"
                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                isCustomPageSize={true}
              />
            )}
          </div>
        </div>



        {/* Modals */}
        <PromoCodeModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(false)}
          promoCode={selectedPromo}
          onSave={handleSavePromoCode}
        />

        <DeletePromoModal
          isOpen={deleteModalOpen}
          toggle={() => setDeleteModalOpen(false)}
          promoCode={selectedPromo}
          onDelete={handleDeletePromoCode}
        />
      </div>
    </div>
  );
};

export default AllPromoCodes;